import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ImagePlus, Loader2, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StoredBarMenuItem } from '@/lib/barMenuItems'

interface BarMenuItemFormProps {
  isOpen: boolean
  zonePrefix: string
  initialItem?: StoredBarMenuItem | null
  onClose: () => void
  onSave: (
    data: Omit<StoredBarMenuItem, 'id' | 'createdAt' | 'updatedAt' | 'category'> & {
      id?: string
    }
  ) => void
}

export function BarMenuItemForm({
  isOpen,
  zonePrefix,
  initialItem,
  onClose,
  onSave,
}: BarMenuItemFormProps) {
  const { t } = useTranslation('admin')

  const [name, setName] = useState(initialItem?.name ?? '')
  const [description, setDescription] = useState(initialItem?.description ?? '')
  const [allergens, setAllergens] = useState(initialItem?.allergens ?? '')
  const [price, setPrice] = useState(initialItem?.price ?? '')
  const [image, setImage] = useState(initialItem?.image ?? '')
  const [previewName, setPreviewName] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [formError, setFormError] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setName(initialItem?.name ?? '')
    setDescription(initialItem?.description ?? '')
    setAllergens(initialItem?.allergens ?? '')
    setPrice(initialItem?.price ?? '')
    setImage(initialItem?.image ?? '')
    setPreviewName('')
    setStatus('idle')
    setFormError(false)
  }, [initialItem, isOpen])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [isOpen])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setFormError(true)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setImage(reader.result as string)
      setPreviewName(file.name)
      setFormError(false)
    }
    reader.readAsDataURL(file)
  }

  const handleRemovePhoto = () => {
    setImage('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetForm = () => {
    setName('')
    setDescription('')
    setAllergens('')
    setPrice('')
    setImage('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim() || !image) {
      setFormError(true)
      return
    }

    setFormError(false)
    setStatus('submitting')

    setTimeout(() => {
      onSave({
        id: initialItem?.id,
        name: name.trim(),
        description: description.trim() || undefined,
        allergens: allergens.trim() || undefined,
        price: price.trim() || undefined,
        image,
      })
      setStatus('success')
      setTimeout(() => {
        resetForm()
        onClose()
      }, 800)
    }, 400)
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bar-menu-item-form-title"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:p-10">
        <div className="mb-6 flex items-center justify-between">
          <h2
            id="bar-menu-item-form-title"
            className="font-display text-2xl font-bold text-ink"
          >
            {initialItem?.id
              ? t(`${zonePrefix}.editDish`)
              : t(`${zonePrefix}.addDish`)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('aria.close')}
            className="rounded-full p-2 text-ink/70 transition-colors hover:bg-cord hover:text-ink"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t(`${zonePrefix}.nameLabel`)}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setFormError(false)
              }}
              placeholder={t(`${zonePrefix}.namePlaceholder`)}
              required
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t(`${zonePrefix}.descriptionLabel`)}
            </span>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value)
                setFormError(false)
              }}
              placeholder={t(`${zonePrefix}.descriptionPlaceholder`)}
              rows={4}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t(`${zonePrefix}.allergensLabel`)}
            </span>
            <input
              type="text"
              value={allergens}
              onChange={(e) => {
                setAllergens(e.target.value)
                setFormError(false)
              }}
              placeholder={t(`${zonePrefix}.allergensPlaceholder`)}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t(`${zonePrefix}.priceLabel`)}
            </span>
            <input
              type="text"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value)
                setFormError(false)
              }}
              placeholder={t(`${zonePrefix}.pricePlaceholder`)}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </label>

          <div className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t(`${zonePrefix}.photoLabel`)}
            </span>
            <div
              className={cn(
                'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors',
                image
                  ? 'border-bar bg-cream'
                  : 'border-cord bg-cream hover:border-bar hover:bg-cream/80'
              )}
            >
              {image ? (
                <div className="w-full space-y-4">
                  <img
                    src={image}
                    alt={t(`${zonePrefix}.photoPreviewAlt`)}
                    className="mx-auto h-64 rounded-lg object-cover aspect-[3/4]"
                  />
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-sm font-medium text-ink">
                      {previewName || t(`${zonePrefix}.photoPreviewAlt`)}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="rounded-full bg-pizza px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-pizza-dark"
                    >
                      {t(`${zonePrefix}.removePhoto`)}
                    </button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer space-y-2">
                  <ImagePlus
                    className="mx-auto h-10 w-10 text-bar/60"
                    aria-hidden="true"
                  />
                  <span className="block text-sm font-semibold text-ink">
                    {t(`${zonePrefix}.photoHint`)}
                  </span>
                  <span className="block text-xs text-muted">
                    {t(`${zonePrefix}.photoFormats`)}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
              )}
            </div>
          </div>

          {formError && (
            <p className="text-sm font-medium text-pizza">
              {t(`${zonePrefix}.errorFill`)}
            </p>
          )}

          {status === 'success' && (
            <div className="flex items-center gap-2 rounded-xl bg-bar/10 px-4 py-3 text-sm font-semibold text-ink">
              <Check className="h-4 w-4" aria-hidden="true" />
              {t(`${zonePrefix}.successMessage`)}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full rounded-full bg-bar px-8 py-3 text-sm font-bold text-white shadow-lg transition-colors hover:bg-bar-dark disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
            >
              {status === 'submitting' ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t(`${zonePrefix}.submitting`)}
                </span>
              ) : (
                t(`${zonePrefix}.submit`)
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
